import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { EnvironmentService } from 'src/app/shared/environment.service';

export interface AvatarMeta {
  backgroundColor: string;
  fontColor: string;
  text: string;
}

export interface PendingChatTarget {
  roomId?: string | null;
  staffId?: string | null;
  staffName?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class MessagesService {
  messagesBasePath = 'chat';
  chatroomsPath = 'rooms';
  messagesPath = 'messages';

  private openChatTargetSubject = new Subject<PendingChatTarget>();
  openChatTarget$ = this.openChatTargetSubject.asObservable();

  constructor(public environmentService: EnvironmentService) { }

  requestOpenChat(target: PendingChatTarget): void {
    if (target && (target.roomId || target.staffId)) {
      this.openChatTargetSubject.next(target);
    }
  }

  resolveImageUrl(imageUrl: string | null | undefined): string {
    if (!imageUrl) {
      return '';
    }

    if (/^(https?:)?\/\//.test(imageUrl) || imageUrl.startsWith('assets/') || imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) {
      return imageUrl;
    }

    return `${this.environmentService.imgReviewUrl}/${imageUrl.replace(/^\/+/, '')}`;
  }

  getAvatarMeta(name: string | null | undefined): AvatarMeta {
    const displayName = (name || '').trim();
    const text = this.getInitials(displayName);
    const avatarColor = this.pickAvatarColor(displayName || text || 'User');
    return {
      backgroundColor: this.getBg(avatarColor),
      fontColor: avatarColor,
      text,
    };
  }

  private getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private pickAvatarColor(name: string): string {
    const palette = ['#1D4ED8', '#047857', '#7C3AED', '#DC2626', '#0C4A6E', '#B45309', '#0F172A', '#6D28D9', '#0EA5E9'];
    const hash = Array.from(name).reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return palette[hash % palette.length];
  }

  private getBg(hex: string, mix = 0.85): string {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const mixed = [r, g, b].map((channel) => Math.round(channel + (255 - channel) * mix));
    return `rgb(${mixed[0]}, ${mixed[1]}, ${mixed[2]})`;
  }

  buildUrlWithParams(basePath: string, params: any): string {
    let urlParams = '';
    for (const key in params) {
      if (
        Object.prototype.hasOwnProperty.call(params, key) &&
        params[key] !== undefined &&
        params[key] !== ''
      ) {
        const value = encodeURIComponent(params[key]);
        urlParams += urlParams === '' ? `?${key}=${value}` : `&${key}=${value}`;
      }
    }

    return `${basePath}${urlParams}`;
  }

  getChatRooms(params: any) {
    let url = this.buildUrlWithParams(`${this.messagesBasePath}/${this.chatroomsPath}`, params);
    return this.environmentService.get(url, params);
  }
  getCombinedChatList(params: any) {
    let url = this.buildUrlWithParams(`${this.messagesBasePath}/contacts`, params);
    return this.environmentService.get(url, params);
  }

  getRoomById(roomId: string) {
    return this.environmentService.get(`${this.messagesBasePath}/${this.chatroomsPath}/${roomId}`);
  }

  createChatRooms(params: any) {
    let url = `${this.messagesBasePath}/${this.chatroomsPath}`;
    return this.environmentService.postFormData(url, params);
  }

  updateGroupChatInfo(roomId: string, params: Partial<{ roomName: string; roomDescription: string; adminOnly: boolean }>) {
    const baseUrl = `${this.messagesBasePath}/${this.chatroomsPath}/${roomId}`;
    const url = this.buildUrlWithParams(baseUrl, params);
    return this.environmentService.patchWithoutBody(url);
  }

  updateGroupChatImage(roomId: string, image: File | null) {
    const url = `${this.messagesBasePath}/${this.chatroomsPath}/${roomId}/image`;
    const formData = new FormData();
    if (image) {
      formData.append('image', image);
    } else {
      const emptyBlob = new Blob([], { type: 'image/jpeg' });
      formData.append('image', emptyBlob, 'empty.jpg');
    }
    return this.environmentService.patchFormData(url, formData);
  }

  addUsersToChatRoom(payload: { userID: string[]; roomID: string }) {
    return this.environmentService.post(`${this.messagesBasePath}/user`, payload);
  }

  removeUserFromChatRoom(payload: { userID: string; roomID: string; isExit: boolean }) {
    return this.environmentService.deleteWithPayload(`${this.messagesBasePath}/remove/User`, payload);
  }

  getChatListUsingRoomId(params: any) {
    const url = this.buildUrlWithParams(
      `${this.messagesBasePath}/${this.messagesPath}`,
      params
    );

    return this.environmentService.get(url, params);
  }


  sendMessage(payload: any) {
    let url = `${this.messagesBasePath}/room/message`;
    return this.environmentService.postFormData(url, payload);
  }

  updateMessage(params: any) {
    let url = this.buildUrlWithParams(`${this.messagesBasePath}/${this.chatroomsPath}/messages/update-content`, params);
    return this.environmentService.put(url, params);
  }

  updateRoomStatus(params: any) {
    let url = this.buildUrlWithParams(`${this.messagesBasePath}/messages/update-status`, params);
    return this.environmentService.put(url, params);
  }

  makeAsGroupAdmin(params: any) {
    let url = this.buildUrlWithParams(`${this.messagesBasePath}/${this.chatroomsPath}/make-admin`, params);
    return this.environmentService.put(url, params);
  }
  updateMessageStatus(params: any) {
    let url = this.buildUrlWithParams(`${this.messagesBasePath}/messages/update-status`, params);
    return this.environmentService.put(url, params);
  }
  deleteMessage(messageId: string) {
    return this.environmentService.delete(`${this.messagesBasePath}/message/${messageId}`);
  }
  createReaction(data: any) {
    return this.environmentService.post(`${this.messagesBasePath}/message/reaction`, data);
  }

  blockClearRoom(roomId: string, action: string, blockStaffId?: string) {
    let params: any = { action };
    if (blockStaffId) {
      params.blockStaffId = blockStaffId;
    }
    const url = this.buildUrlWithParams(`${this.messagesBasePath}/${this.chatroomsPath}/messages/block-clear/${roomId}`, params);
    return this.environmentService.patch(url, {});
  }

}
